import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { ActivityType } from "../../../domain/ActivityType";
import { EventNotification } from "../../../domain/EventNotification";
import { authSelector } from "../../../redux/selectors";
import { eventNotificationsService } from "../../../services";
import { fieldLabels } from "../../sections/fieldLabels";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import {
  ActionType,
  AssignActivityBody,
  AttachmentActivityBody,
  AttachmentBodyProps,
  ChangeActivityBody,
  CloseActivityBody,
  MessageActivityBody,
  ShareBody,
} from "./EventNotificationActivityFeedTypes";

export const buildDefinitions = (
  eventNotification: EventNotification | null,
  onClick: (entity: ActionType | Activity | null) => void
) => {
  if (!eventNotification) {
    return [];
  }

  const enCreated: ActivityFeedItemDefinition<{ summary: string }> = {
    activityType: ActivityType.EventNotificationCreated,
    Body: ({ body }) => <>{body.summary}</>,
    options: [],
  };

  const enAoUpdate: ActivityFeedItemDefinition<ChangeActivityBody> = {
    activityType: ActivityType.EventNotificationAoDetailsUpdated,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => `"${fieldLabels[item.field]}"`).join(",")}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick,
      },
    ],
  };

  const enOfqualUpdate: ActivityFeedItemDefinition<ChangeActivityBody> = {
    activityType: ActivityType.EventNotificationOfqualDetailsUpdated,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => `"${fieldLabels[item.field]}"`).join(",")}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick,
      },
    ],
  };

  const enClosed: ActivityFeedItemDefinition<CloseActivityBody> = {
    activityType: ActivityType.EventNotificationClosed,
    Body: ({ body }) => (
      <>
        <ReactMarkdown>{body.reason}</ReactMarkdown>
      </>
    ),
    options: [],
  };

  const enReopened: ActivityFeedItemDefinition<CloseActivityBody> = {
    activityType: ActivityType.EventNotificationReopened,
    Body: ({ body }) => <>{body.summary}</>,
    options: [],
  };

  const enAssigned: ActivityFeedItemDefinition<AssignActivityBody> = {
    activityType: ActivityType.EventNotificationAssigned,
    Body: ({ body }) => <>{body.summary}</>,
    options: [],
  };

  const enMessageOfqual: ActivityFeedItemDefinition<MessageActivityBody> = {
    activityType: ActivityType.EventNotificationMessageOfqual,
    Body: ({ body }) => <>{<ReactMarkdown>{body.message}</ReactMarkdown>}</>,
    options: [],
  };

  const enMessageAo: ActivityFeedItemDefinition<MessageActivityBody> = {
    activityType: ActivityType.EventNotificationMessageAo,
    Body: ({ body }) => <>{<ReactMarkdown>{body.message}</ReactMarkdown>}</>,
    options: [],
    align: "left",
  };

  const EnAttachmentBody: React.FC<AttachmentBodyProps> = ({
    bodyProps,
    token,
  }) => {
    const { activity, body } = bodyProps;

    const buildGetBlob = (blobId: string) => () =>
      eventNotification &&
      eventNotificationsService.downloadAttachment(
        eventNotification.id,
        activity.id,
        blobId,
        { token }
      );

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((document) => (
            <li key={document.blobId} style={{ listStyleType: "none" }}>
              <DownloadLink
                fileName={document.fileName}
                getBlob={buildGetBlob(document.blobId)}
              />
            </li>
          ))}
        </ul>
        {body.note && (
          <div data-test="activity-attach-note">
            <b>
              <em>Note:</em>
            </b>{" "}
            <ReactMarkdown>{body.note}</ReactMarkdown>
          </div>
        )}
      </>
    );
  };

  const enAttachmentOfqual: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.EventNotificationDocumentOfqual,
    Body: ({ body, activity }) => {
      const { token } = useSelector(authSelector);
      return <EnAttachmentBody token={token} bodyProps={{ activity, body }} />;
    },
  };

  const enAttachmentAo: ActivityFeedItemDefinition<AttachmentActivityBody> = {
    activityType: ActivityType.EventNotificationDocumentAo,
    Body: ({ body, activity }) => {
      const { token } = useSelector(authSelector);
      return <EnAttachmentBody token={token} bodyProps={{ activity, body }} />;
    },
    align: "left",
  };

  const share: ActivityFeedItemDefinition<ShareBody> = {
    activityType: ActivityType.ShareRecord,
    Body: ({ body }) => (
      <>
        <b>Shared with:</b> {body.whitelist.map((item) => `${item}`).join(",")}
      </>
    ),
    options: [],
  };

  const unShare: ActivityFeedItemDefinition<ShareBody> = {
    activityType: ActivityType.UnSharedRecord,
    Body: ({ body }) => (
      <>
        <b>UnShared with:</b>{" "}
        {body.whitelist.map((item) => `${item}`).join(",")}
      </>
    ),
    options: [],
  };

  return [
    enCreated,
    enAoUpdate,
    enOfqualUpdate,
    enClosed,
    enReopened,
    enAssigned,
    enMessageOfqual,
    enMessageAo,
    enAttachmentOfqual,
    enAttachmentAo,
    share,
    unShare,
  ];
};
